import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Button,
  makeStyles,
  shorthands,
  Text,
  tokens,
  Input,
  Badge,
  Tooltip,
  Spinner,
  FluentProvider,
  webLightTheme,
} from "@fluentui/react-components";
import {
  DismissRegular,
  SaveRegular,
  InfoRegular,
  ReOrderDotsVerticalRegular,
} from "@fluentui/react-icons";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
  DraggableProvided,
  DraggableStateSnapshot,
} from "@hello-pangea/dnd";
import { IRankableAssignment } from "../pages/Policies";
import apiClient from "../services/apiClient";
import useEventTracker from "../hooks/useEventTracker";
import { ElementButton, PageName } from "../tracking/tracking.types";
import { ComponentEvent, ManageRank } from "../hooks/eventTracker.types";

const useStyles = makeStyles({
  dialogContent: {
    minWidth: "700px",
    maxWidth: "900px",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
  },
  loadingIndicator: {
    display: "flex",
    pointerEvents: "none",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: "100%",
  },
  closeButton: {
    position: "absolute",
    top: "12px",
    right: "12px",
    minWidth: "auto",
  },
  infoSection: {
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.padding("12px"),
    ...shorthands.borderRadius("6px"),
    marginBottom: "20px",
  },
  rankInput: {
    width: "80px",
    textAlign: "center",
  },
  actions: {
    display: "flex",
    ...shorthands.gap("8px"),
  },
  policyName: {
    fontWeight: "600",
  },
  changeIndicator: {
    marginLeft: "8px",
    fontSize: "12px",
    color: tokens.colorPaletteRedForeground1,
  },
  draggableRow: {
    display: "flex",
    alignItems: "center",
    ...shorthands.padding("12px"),
    ...shorthands.gap("12px"),
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke1),
    ...shorthands.borderRadius("4px"),
    marginBottom: "8px",
    backgroundColor: tokens.colorNeutralBackground1,
    "&:hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  draggingRow: {
    backgroundColor: tokens.colorBrandBackground2,
    boxShadow: "0 8px 16px rgba(0,0,0,0.2)",
    opacity: 0.9,
  },
  dragHandle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "grab",
    color: tokens.colorNeutralForeground2,
    "&:hover": {
      color: tokens.colorNeutralForeground1,
    },
    "&:active": {
      cursor: "grabbing",
    },
  },
  rowCell: {
    display: "flex",
    alignItems: "center",
    ...shorthands.gap("8px"),
  },
});

interface RankManagementDialogProps {
  open: boolean;
  onClose: () => void;
  onSave?: (
    updatedPolicies: Array<{ originalRank: number } & IRankableAssignment>
  ) => void;
  onSavingChange?: (saving: boolean) => void;
}

// Helper function to get cell style with drag state
export const getCellStyle = (
  baseStyle: React.CSSProperties,
  isDragging: boolean
) => ({
  ...baseStyle,
  ...(isDragging ? { display: "flex", alignItems: "center", gap: "8px" } : {}),
});

// Helper function to get drag handle style
export const getDragHandleStyle = (isDragging: boolean) => ({
  flexShrink: 0,
  cursor: isDragging ? "grabbing" : "grab",
  ...(isDragging
    ? {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: tokens.colorNeutralForeground2,
      }
    : {}),
});

const DEFAULT_RANK = 999;

export const RankManagementDialog: React.FC<RankManagementDialogProps> = ({
  open,
  onClose,
  onSave,
  onSavingChange,
}) => {
  const styles = useStyles();
  const [assignments, setAssignments] = useState<IRankableAssignment[]>([]);
  const [policyDialogFirstTime, setPolicyDialogFirstTime] = useState(false);
  const [saving, setSaving] = useState(false);
  // Track which input is currently focused (used for managing focus state)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [editingPolicyId, setEditingPolicyId] = useState<string | null>(null);
  const originalRanksRef = useRef<Map<string, number>>(new Map());
  const inputRefs = useRef<Map<string, HTMLInputElement>>(new Map());

  useEffect(() => {
    if (!open) return;

    setAssignments([]);
    setEditingPolicyId(null);
    setPolicyDialogFirstTime(false);

    const abortController = new AbortController();

    (async () => {
      try {
        const response = await apiClient.get(
          "/assignment",
          undefined,
          abortController.signal
        );
        const data = (response?.data || response) as IRankableAssignment[];

        const byOriginalRank = [...(data ?? [])].sort(
          (a, b) => (a.rank ?? DEFAULT_RANK) - (b.rank ?? DEFAULT_RANK)
        );

        const m = new Map<string, number>();
        for (const p of byOriginalRank) {
          m.set(p.groupId, p.rank ?? DEFAULT_RANK);
        }
        originalRanksRef.current = m;

        const normalized = byOriginalRank.map((p, i) => ({
          ...p,
          rank: i + 1,
        }));

        setAssignments(normalized);
        setEditingPolicyId(null);
      } catch (err: any) {
        if (err?.name !== "AbortError") {
          console.error("Failed to load assignments", err);
        }
      }
    })();

    return () => {
      abortController.abort();
    };
  }, [open]);

  // Track component loaded on first render
  useEffect(() => {
    if (open && assignments.length > 0 && !policyDialogFirstTime) {
      setPolicyDialogFirstTime(true);
      const ranks = assignments.map((item) => ({
        rank: item.rank ?? 0,
        policyId: item.groupId,
        groupId: item.groupId,
        domains: item.domainCount,
        type: item.isAdmManaged ? "ADM" : "Teams",
      }));
      handleActionOnComponentLoaded({
        pageName: PageName.POLICIES,
        componentName: "manage-policy-ranks",
        componentType: "dialog",
        ranks,
      });
    }
  }, [open]);

  // === Event Tracking ===
  const eventTracker = useEventTracker();
  const handleActionOnComponentLoaded = useCallback(
    async (event: ComponentEvent) => {
      const tracker = {
        pageName: PageName.POLICIES,
        ...(event.componentName && { componentName: event.componentName }),
        ...(event.componentType && { componentType: event.componentType }),
        ...(event.ranks && { ranks: event.ranks }),
      };
      await eventTracker.eventTrackOnComponentLoaded(tracker as ComponentEvent);
    },
    [eventTracker]
  );

  const handleActionOnClick = useCallback(
    async (elementButton: ElementButton, ranks: ManageRank[]) => {
      await eventTracker.eventTrackOnElementClicked({
        elementName: elementButton,
        elementType: "button",
        pageName: PageName.POLICIES,
        ranks,
      });
    },
    [eventTracker]
  );

  // Track component loaded on first render
  useEffect(() => {
    if (open && assignments.length > 0 && !policyDialogFirstTime) {
      setPolicyDialogFirstTime(true);
      const ranks = assignments.map((item) => ({
        rank: item.rank ?? 0,
        policyId: item.groupId,
        groupId: item.groupId,
        domains: item.domainCount,
        type: item.isAdmManaged ? "ADM" : "Teams",
      }));
      handleActionOnComponentLoaded({
        pageName: PageName.POLICIES,
        componentName: "manage-policy-ranks",
        componentType: "dialog",
        ranks,
      });
    }
  }, [open, assignments, policyDialogFirstTime, handleActionOnComponentLoaded]);

  // === Derived: hasChanges (compare UI sequential rank to original server rank) ===
  const hasChanges = useMemo(() => {
    if (!assignments.length) return false;
    for (const a of assignments) {
      const orig = originalRanksRef.current.get(a.groupId) ?? DEFAULT_RANK;
      // Current UI 'rank' is normalized position; difference => changed
      if ((a.rank ?? DEFAULT_RANK) !== orig) return true;
    }
    return false;
  }, [assignments]);

  // === Handlers ===

  // Change by typing a rank number (use groupId consistently)
  const handleRankChange = (groupId: string, newRank: string) => {
    const parsed = parseInt(newRank, 10);
    if (isNaN(parsed)) return;

    setAssignments((prev) => {
      if (!prev.length) return prev;

      const maxRank = prev.length;
      const clamped = Math.min(Math.max(parsed, 1), maxRank);

      const targetIndex = prev.findIndex((p) => p.groupId === groupId);
      if (targetIndex === -1) return prev;

      const updated = prev.slice();
      const [moved] = updated.splice(targetIndex, 1);
      updated.splice(clamped - 1, 0, moved);

      // Re-normalize 1..N for UI
      return updated.map((p, i) => ({ ...p, rank: i + 1 }));
    });
  };

  // Drag and drop reordering
  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;
    if (sourceIndex === destIndex) return;

    setAssignments((prev) => {
      const updated = prev.slice();
      const [moved] = updated.splice(sourceIndex, 1);
      updated.splice(destIndex, 0, moved);
      return updated.map((p, i) => ({ ...p, rank: i + 1 }));
    });
  };
  const handleSave = async () => {
    setPolicyDialogFirstTime(false);
    setSaving(true);
    onSavingChange?.(true);

    // Close dialog immediately - save continues
    onClose();

    try {
      if (onSave) {
        const payload = assignments.map((p) => ({
          ...p,
          originalRank: originalRanksRef.current.get(p.groupId) ?? p.rank,
        }));
        const ranks = payload.map((item) => ({
          rank: item.rank ?? 0,
          policyId: item.groupId,
          groupId: item.groupId,
          domains: item.domainCount,
          type: item.isAdmManaged ? "ADM" : "Teams",
        }));
        handleActionOnClick(ElementButton.SAVE, ranks);
        await onSave(payload);
      }
    } catch (error) {
      // Swallow or surface—up to you
    } finally {
      setSaving(false);
      onSavingChange?.(false);
    }
  };

  // Reset to original server ranks (not current UI ranks)
  const handleReset = () => {
    setAssignments((prev) => {
      if (!prev.length) return prev;

      const sortedByOriginal = [...prev].sort((a, b) => {
        const ra = originalRanksRef.current.get(a.groupId) ?? DEFAULT_RANK;
        const rb = originalRanksRef.current.get(b.groupId) ?? DEFAULT_RANK;
        return ra - rb;
      });

      // Normalize back to 1..N for UI
      return sortedByOriginal.map((p, i) => ({ ...p, rank: i + 1 }));
    });
  };

  // === Row renderer (unchanged logic; swap originalRanks -> originalRanksRef) ===
  const renderAssignmentRow = (
    rankableAssignment: IRankableAssignment,
    provided: DraggableProvided,
    snapshot: DraggableStateSnapshot,
    hasChanged: boolean, 
  ) => {
    const style: React.CSSProperties = {
      ...provided.draggableProps.style,
      ...(snapshot.isDragging
        ? {
            zIndex: 1000001,
            display: "flex",
            alignItems: "center",
            padding: "12px",
            gap: "12px",
            border: `1px solid ${tokens.colorNeutralStroke1}`,
            borderRadius: "4px",
            marginBottom: "8px",
            backgroundColor: tokens.colorBrandBackground2,
            boxShadow: "0 8px 16px rgba(0,0,0,0.2)",
            opacity: 0.9,
          }
        : {}),
    };

    return (
      <div
        data-testid={`rank-row-${rankableAssignment.groupId}`}
        ref={provided.innerRef}
        {...provided.draggableProps}
        className={snapshot.isDragging ? undefined : `${styles.draggableRow}`}
        style={style}
      >
        <div
          {...provided.dragHandleProps}
          style={getDragHandleStyle(snapshot.isDragging)}
          className={snapshot.isDragging ? undefined : styles.dragHandle} data-testid="rank-management-row-drag-handle"
        >
          <Tooltip
            content="Drag to reorder"
            relationship="label"
            data-testid={`drag-handle-tooltip-${rankableAssignment.groupId}`}
          >
            <ReOrderDotsVerticalRegular />
          </Tooltip>
        </div>

        <div
          style={getCellStyle(
            { width: "100px", flexShrink: 0 },
            snapshot.isDragging
          )}
          className={snapshot.isDragging ? undefined : styles.rowCell} data-testid={`rank-management-row-rank-cell-${rankableAssignment.groupId}`}
        >
          <Input
            type="number"
            min={1}
            max={assignments.length}
            value={rankableAssignment.rank?.toString() || ""}
            onFocus={() => setEditingPolicyId(rankableAssignment.groupId)}
            onBlur={() =>
              setEditingPolicyId((prev) =>
                prev === rankableAssignment.groupId ? null : prev
              )
            }
            onChange={(_, data) =>
              handleRankChange(rankableAssignment.groupId, data.value)
            }
            className={styles.rankInput}
            style={
              snapshot.isDragging
                ? { width: "80px", textAlign: "center" }
                : undefined
            }
            ref={(element) => {
              if (element) {
                inputRefs.current.set(rankableAssignment.groupId, element);
              } else {
                inputRefs.current.delete(rankableAssignment.groupId);
              }
            }}
            data-testid={`rank-management-rank-input-${rankableAssignment.groupId}`}
          />
          {hasChanged && (
            <Text
              className={
                snapshot.isDragging ? undefined : styles.changeIndicator
              }
              style={
                snapshot.isDragging
                  ? {
                      marginLeft: "8px",
                      fontSize: "12px",
                      color: tokens.colorPaletteRedForeground1,
                    }
                  : undefined
              }
              data-testid={`rank-management-changed-indicator-${rankableAssignment.groupId}`}
            >
              *
            </Text>
          )}
        </div>

        <div
          style={getCellStyle({ flex: 1, minWidth: 0 }, snapshot.isDragging)}
          className={snapshot.isDragging ? undefined : styles.rowCell} data-testid={`rank-management-row-policy-cell-${rankableAssignment.groupId}`}
        >
          <Text
            className={snapshot.isDragging ? undefined : styles.policyName}
            style={snapshot.isDragging ? { fontWeight: "600" } : undefined}
            data-testid={`rank-management-policy-name-${rankableAssignment.groupId}`}
          >
            {rankableAssignment.policyName}
          </Text>
        </div>

        <div
          style={getCellStyle(
            { width: "200px", flexShrink: 0 },
            snapshot.isDragging
          )}
          className={snapshot.isDragging ? undefined : styles.rowCell} data-testid={`rank-management-row-group-cell-${rankableAssignment.groupId}`}
        >
          <Badge appearance="filled" color="important" data-testid={`rank-management-group-badge-${rankableAssignment.groupId}`}>
            {rankableAssignment.groupName}
          </Badge>
        </div>

        <div
          style={getCellStyle(
            { width: "120px", flexShrink: 0 },
            snapshot.isDragging
          )}
          className={snapshot.isDragging ? undefined : styles.rowCell} data-testid={`rank-management-row-domain-count-cell-${rankableAssignment.groupId}`}
        >
          <Badge appearance="filled" color="brand" data-testid={`rank-management-domain-count-badge-${rankableAssignment.groupId}`}>
            {rankableAssignment.domainCount} domains
          </Badge>
        </div>

        <div
          style={getCellStyle(
            { width: "120px", flexShrink: 0 },
            snapshot.isDragging
          )}
          className={snapshot.isDragging ? undefined : styles.rowCell} data-testid={`rank-management-row-managed-type-cell-${rankableAssignment.groupId}`}
        >
          <Badge
            appearance="tint"
            color={rankableAssignment.isAdmManaged ? "success" : "informative"}
            data-testid={`rank-management-managed-type-badge-${rankableAssignment.groupId}`}
          >
            {rankableAssignment.isAdmManaged ? "ADM Managed" : "Teams Managed"}
          </Badge>
        </div>
      </div>
    );
  };

  const LoadingIndicator = () => {
    return (
      <div className={styles.loadingIndicator} data-testid="rank-management-loading-indicator">
        <Spinner size="extra-large" data-testid="rank-management-loading-spinner" />
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(_, data) => !data.open && onClose()} data-testid="rank-management-dialog-root">
      <DialogSurface className={styles.dialogContent} data-testid="rank-management-dialog">
        <DialogBody data-testid="rank-management-dialog-body">
          <DialogTitle data-testid="rank-management-dialog-title">
            Manage Policy Assignment Ranks
            <Button
              appearance="subtle"
              icon={<DismissRegular />}
              onClick={onClose}
              className={styles.closeButton}
              data-testid="rank-management-close"
            />
          </DialogTitle>

          <DialogContent data-testid="rank-management-dialog-content">
            <div className={styles.infoSection} data-testid="rank-management-info-section">
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "8px",
                }} data-testid="rank-management-info-content"
              >
                <InfoRegular
                  style={{
                    color: tokens.colorBrandForeground1,
                    marginTop: "2px",
                  }}
                />
                <div data-testid="rank-management-info-text">
                  <Text
                    weight="semibold"
                    style={{ display: "block", marginBottom: "4px" }}
                    data-testid="rank-management-info-title"
                  >
                    Policy Rank Management
                  </Text>
                  <Text size={200} data-testid="rank-management-info-description">
                    Lower rank numbers have higher priority when Teams evaluates
                    policies. Rank 1 is the highest priority. Policies are
                    evaluated in order from lowest to highest rank number.
                  </Text>
                </div>
              </div>
            </div>

            <div
              style={{ height: "350px", maxHeight: "350px", overflowY: "auto" }} data-testid="rank-management-list-container"
            >
              {assignments.length > 0 ? (
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable
                    droppableId="policy-list"
                    renderClone={(provided, snapshot, rubric) => {
                      const policy = assignments[rubric.source.index];
                      const hasChanged =
                        (policy.rank ?? DEFAULT_RANK) !==
                        (originalRanksRef.current.get(policy.groupId) ??
                          DEFAULT_RANK);
                      return (
                        <FluentProvider theme={webLightTheme}>
                          {renderAssignmentRow(
                            policy,
                            provided,
                            snapshot,
                            hasChanged,
                          )}
                        </FluentProvider>
                      );
                    }}
                  >
                    {(provided) => (
                      <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        style={{ position: "relative" }} data-testid="rank-management-droppable-list"
                      >
                        {assignments.map((assignment, index) => {
                          const hasChanged =
                            (assignment.rank ?? DEFAULT_RANK) !==
                            (originalRanksRef.current.get(assignment.groupId) ??
                              DEFAULT_RANK);

                          return (
                            <Draggable
                              key={assignment.groupId}
                              draggableId={assignment.groupId}
                              index={index}
                            >
                              {(provided, snapshot) =>
                                renderAssignmentRow(
                                  assignment,
                                  provided,
                                  snapshot,
                                  hasChanged,
                                )
                              }
                            </Draggable>
                          );
                        })}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              ) : (
                <LoadingIndicator />
              )}
            </div>

            {hasChanges && (
              <div
                style={{
                  marginTop: "12px",
                  padding: "8px",
                  backgroundColor: tokens.colorPaletteYellowBackground1,
                  borderRadius: "4px",
                }} data-testid="rank-management-unsaved-changes-message"
              >
                <Text size={200} data-testid="rank-management-unsaved-changes-text">
                  * You have unsaved changes. Click Save to apply the new ranks.
                </Text>
              </div>
            )}
          </DialogContent>

          <DialogActions data-testid="rank-management-dialog-actions">
            <Button
              appearance="secondary"
              onClick={handleReset}
              disabled={!hasChanges || saving}
              data-testid="rank-management-reset"
            >
              Reset Changes
            </Button>
            <Button appearance="secondary" onClick={onClose} data-testid="rank-management-cancel">
              Cancel
            </Button>
            <Button
              appearance="primary"
              icon={saving ? <Spinner size="tiny" data-testid="rank-management-save-spinner" /> : <SaveRegular />}
              onClick={handleSave}
              disabled={!hasChanges || saving}
              data-testid="rank-management-save"
            >
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
